import { useEffect, useState } from 'react';
import { designFontParts } from '@sarnia/shared';
import { Icon } from '../../../components/ui/Icon';
import type { PanelCtx } from '../panelTypes';
import './panels.css';

interface FontSelection {
  heading?: string;
  body?: string;
}

const FONT_GROUPS: Array<{ label: string; fonts: string[] }> = [
  { label: 'Sans-serif', fonts: ['Inter', 'Poppins', 'Montserrat', 'Raleway', 'Nunito', 'Lato', 'DM Sans', 'Space Grotesk', 'Outfit'] },
  { label: 'Display', fonts: ['Oswald', 'Bebas Neue', 'Anton', 'Abril Fatface', 'Pacifico', 'Caveat'] },
  { label: 'Serif', fonts: ['Playfair Display', 'DM Serif Display', 'Lora', 'Merriweather', 'Cormorant Garamond', 'Libre Baskerville'] },
  { label: 'Mono & retro', fonts: ['JetBrains Mono', 'Press Start 2P'] }
];

interface FontsPanelProps {
  ctx: PanelCtx;
}

export default function FontsPanel({ ctx }: FontsPanelProps) {
  const [fonts, setFonts] = useState<FontSelection>(() => {
    const source = ctx.props.fonts;
    return {
      ...(source?.heading ? { heading: source.heading } : {}),
      ...(source?.body ? { body: source.body } : {}),
    };
  });

  useEffect(() => {
    const source = ctx.props.fonts;
    setFonts((prev) => {
      const next: FontSelection = {
        ...(source?.heading ? { heading: source.heading } : {}),
        ...(source?.body ? { body: source.body } : {}),
      };
      if (prev.heading === next.heading && prev.body === next.body) return prev;
      return next;
    });
  }, [ctx.props.fonts]);

  useEffect(() => {
    const parts = designFontParts(
      fonts.heading || fonts.body ? { heading: fonts.heading, body: fonts.body } : undefined
    );
    const linkId = 'dwp-fonts-sample-link';
    let link = document.getElementById(linkId) as HTMLLinkElement | null;
    if (!parts.href) {
      if (link) link.remove();
      return;
    }
    if (!link) {
      link = document.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    if (link.href !== parts.href) link.href = parts.href;
  }, [fonts]);

  const handleChange = (key: 'heading' | 'body', value: string) => {
    const next: FontSelection = { ...fonts };
    if (value === '') {
      delete next[key];
    } else {
      next[key] = value;
    }
    setFonts(next);
    const parts = designFontParts(next);
    ctx.post({ type: 'fonts', href: parts.href, css: parts.css });
    ctx.update({ fonts: next });
  };

  const reset = () => {
    setFonts({});
    const parts = designFontParts(undefined);
    ctx.post({ type: 'fonts', href: parts.href, css: parts.css });
    ctx.update({ fonts: {} });
  };

  const heading = fonts.heading ?? '';
  const body = fonts.body ?? '';

  return (
    <div className="dwp-panel">
      <h2 className="dwp-heading">Fonts</h2>
      <p className="dwp-help">Set the typefaces used across your page.</p>

      <div className="dwp-font-group">
        <div className="dwp-field">
          <label className="dwp-label" htmlFor="dwp-font-heading">Headings</label>
          <select
            id="dwp-font-heading"
            className="dwp-select"
            value={heading}
            onChange={(event) => handleChange('heading', event.target.value)}
          >
            <option value="">Design default</option>
            {FONT_GROUPS.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.fonts.map((font) => (
                  <option key={font} value={font}>{font}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div
          className="dwp-font-sample"
          style={{ fontFamily: heading ? `'${heading}',sans-serif` : 'var(--font-heading)' }}
        >
          The quick brown fox
        </div>
      </div>

      <div className="dwp-font-group">
        <div className="dwp-field">
          <label className="dwp-label" htmlFor="dwp-font-body">Body text</label>
          <select
            id="dwp-font-body"
            className="dwp-select"
            value={body}
            onChange={(event) => handleChange('body', event.target.value)}
          >
            <option value="">Design default</option>
            {FONT_GROUPS.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.fonts.map((font) => (
                  <option key={font} value={font}>{font}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div
          className="dwp-font-sample"
          style={{ fontFamily: body ? `'${body}',sans-serif` : 'var(--font-body)' }}
        >
          The quick brown fox
        </div>
      </div>

      <button type="button" className="dwp-reset" onClick={reset}>
        <Icon name="undo" size={14} />
        <span>Reset fonts</span>
      </button>
    </div>
  );
}
